import { useState, useEffect, useCallback, useRef } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import { useFormContext } from 'react-hook-form';

import { LabelStyled } from 'approot/shared/styles/inputs.style';
import {
  DatePickerStyled,
  DatePickerFieldTriggerStyled,
  DatePickerPopper,
} from './date-picker.style';
import { Props, DATE_FORMAT } from './date-picker.constants';
import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import { useKeyBoardTrap } from 'approot/shared/ally/keyboard.hooks';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { DateText } from 'approot/shared/dates/dates';
import { DatePickerRangeFieldStyled } from './date-picker-range-field.style';
import { addToRange } from './date-picker.utils';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { Box } from 'approot/shared/design-system/box/box.style';

const DateRangePicker = ({
  fromField,
  toField,
  fromFieldValue,
  toFieldValue,
  fromMonth,
  toMonth,
  disabledDays,
  selectedDays,
  onRangeChange,
}: Props) => {
  const { register, unregister, setValue } = useFormContext();

  const fromRef = useRef<HTMLButtonElement | null>(null);
  const toRef = useRef<HTMLButtonElement | null>(null);

  const [isEndRangePicker, setIsEndRangePicker] = useState<boolean>(false);
  const [isStartSelected, setIsStartSelected] = useState<boolean>(false);
  const [enteredTo, setEnteredTo] = useState<Date | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const isMediaUpSmall = useMediaQuery(MEDIA_SM);

  const {
    styles,
    attributes,
    popperElement,
    setReferenceElement,
    setPopperElement,
  } = useContextualSurface(isVisible, setIsVisible);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setIsStartSelected(false);
    setEnteredTo(null);
  }, [setIsVisible, setIsStartSelected, setEnteredTo]);

  useKeyBoardTrap({
    containerRef: popperElement,
    triggerRef: isEndRangePicker ? toRef.current : fromRef.current,
    isActive: isVisible,
    onOpenCloseDelay: 0,
    initialFocus: false,
    close: handleClose,
  });

  const updateRange = useCallback(
    (range: DateRange) => {
      if (range.from && range.to) {
        setValue(fromField, format(range.from, DATE_FORMAT), {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(toField, format(range.to, DATE_FORMAT), {
          shouldValidate: true,
          shouldDirty: true,
        });
        onRangeChange(range);
      }
    },
    [fromField, toField, setValue, onRangeChange]
  );

  const handleChange = useCallback(
    (date: Date) => {
      const selected = selectedDays;

      // handle picking an end date
      if (isEndRangePicker) {
        updateRange({
          from: selected.from,
          to: date,
        });
        handleClose();
        return;
      }

      // handle earlier start date
      if (
        !isStartSelected &&
        (isBefore(date, selected.from!) || isBefore(date, selected.to!))
      ) {
        updateRange({
          from: date,
          to: selected.to,
        });
        handleClose();
        return;
      }

      // handle start date after end date
      if (!isStartSelected && isAfter(date, selected.to!)) {
        updateRange({
          from: date,
          to: date,
        });
        setIsStartSelected(true);
        return;
      }

      const range = addToRange(date, selected);
      if (range) {
        updateRange(range);
      }
      handleClose();
    },
    [isEndRangePicker, isStartSelected, selectedDays, handleClose, updateRange]
  );

  const handleDayMouseEnter = useCallback(
    (day: Date) => {
      if (isStartSelected) {
        setEnteredTo(day);
      }
    },
    [isStartSelected, setEnteredTo]
  );

  const endModifier =
    !isEndRangePicker && enteredTo && isAfter(enteredTo, selectedDays.from!)
      ? enteredTo
      : selectedDays.to;
  const modifiers = {
    start: selectedDays.from || new Date(),
    end: endModifier || new Date(),
  };
  const selected = {
    from: selectedDays.from,
    to: endModifier,
  };

  const disabled = isEndRangePicker
    ? [
        {
          before: selectedDays.from || new Date(),
        },
        {
          after: toMonth || new Date(),
        },
      ]
    : disabledDays;

  useEffect(() => {
    register(fromField);
    register(toField);

    return () => {
      unregister(fromField);
      unregister(toField);
    };
  }, [fromField, toField, register, unregister]);

  return (
    <>
      <DatePickerRangeFieldStyled>
        <Box $marginBottom={isMediaUpSmall ? 24 : 16}>
          <LabelStyled htmlFor={fromField}>Start date</LabelStyled>
          <DatePickerFieldTriggerStyled>
            <SecondaryButton
              type="button"
              ref={fromRef}
              id={fromField}
              name={fromField}
              data-selector={fromField}
              aria-haspopup
              onClick={() => {
                setReferenceElement(fromRef.current);
                if (isEndRangePicker) {
                  handleClose();
                  setIsEndRangePicker(false);
                  setIsVisible(true);
                } else {
                  setIsEndRangePicker(false);
                  setIsVisible(!isVisible);
                  if (isVisible) {
                    handleClose();
                  }
                }
              }}
            >
              <DateText date={fromFieldValue} isShortMonth />
            </SecondaryButton>
          </DatePickerFieldTriggerStyled>
        </Box>
        <Box $marginBottom={isMediaUpSmall ? 24 : 16}>
          <LabelStyled htmlFor={toField}>End date</LabelStyled>
          <DatePickerFieldTriggerStyled>
            <SecondaryButton
              type="button"
              ref={toRef}
              id={toField}
              name={toField}
              data-selector={toField}
              aria-haspopup
              onClick={() => {
                setReferenceElement(toRef.current);
                if (!isEndRangePicker) {
                  handleClose();
                  setIsEndRangePicker(true);
                  setIsVisible(true);
                } else {
                  setIsEndRangePicker(true);
                  setIsVisible(!isVisible);
                  if (isVisible) {
                    handleClose();
                  }
                }
              }}
            >
              <DateText date={toFieldValue} isShortMonth />
            </SecondaryButton>
          </DatePickerFieldTriggerStyled>
        </Box>
      </DatePickerRangeFieldStyled>
      {isVisible && (
        <DatePickerPopper
          ref={setPopperElement}
          role="dialog"
          style={styles.popper}
          tabIndex={-1}
          {...attributes.popper}
        >
          <DatePickerStyled>
            <DayPicker
              mode="range"
              initialFocus
              weekStartsOn={1}
              numberOfMonths={2}
              disabled={disabled}
              selected={selected}
              modifiers={modifiers}
              fromMonth={fromMonth}
              toMonth={toMonth}
              defaultMonth={fromFieldValue}
              onDayClick={(day, modifiers, e) => {
                if (modifiers.disabled || modifiers.outside) {
                  e.stopPropagation();
                  e.preventDefault();
                  return false;
                }
                handleChange(day);
              }}
              onDayMouseEnter={handleDayMouseEnter}
            />
          </DatePickerStyled>
        </DatePickerPopper>
      )}
    </>
  );
};

export default DateRangePicker;
